import { useEffect, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, GlassCard, Input } from '@jumpstone/ui-react';
import type { UsePayrollSettingsResult } from '../../data/use_payroll_settings';
import {
  buildLoanPolicyPatch,
  formatBasisPointsAsPercentInput,
  isEmptyPatch,
  isValidAnnualRateBps,
  isValidInstallmentCount,
  parseIntegerInput,
  parsePercentToBasisPoints,
  type RealPayrollSettingsRecord,
} from '../../data/wagepro_store_payroll_settings';
import { LOAN_KIND_KEY } from '../../lib/label_keys';
import { PayrollSettingsStateGate } from './payroll_settings_state_gate';

interface LoanDraft {
  ratePercentText: string;
  installmentsText: string;
}

function draftFromSettings(settings: RealPayrollSettingsRecord): {
  salary_advance: LoanDraft;
  staff_loan: LoanDraft;
} {
  return {
    salary_advance: {
      ratePercentText: formatBasisPointsAsPercentInput(
        settings.salary_advance_annual_rate_bps,
      ),
      installmentsText: String(settings.salary_advance_max_installments),
    },
    staff_loan: {
      ratePercentText: formatBasisPointsAsPercentInput(
        settings.staff_loan_annual_rate_bps,
      ),
      installmentsText: String(settings.staff_loan_max_installments),
    },
  };
}

/**
 * Data Centre's Loans tab (#22730) — salary-advance and staff-loan default
 * annual interest rate + maximum instalment count, real and editable.
 * Reads/writes `salary_advance_annual_rate_bps`/`_max_installments` and
 * `staff_loan_annual_rate_bps`/`_max_installments` through
 * `usePayrollSettings()`. Rates are basis points on the wire
 * (docs/money-rules.md); the two rows edit a percentage string for human
 * readability, converted at the boundary by
 * `parsePercentToBasisPoints`/`formatBasisPointsAsPercentInput`
 * (`wagepro_store_payroll_settings.ts`) — never floats in the saved patch.
 */
export function DataCentreLoanSection({
  payrollSettings,
}: {
  payrollSettings: UsePayrollSettingsResult;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [draft, setDraft] = useState<{
    salary_advance: LoanDraft;
    staff_loan: LoanDraft;
  }>({
    salary_advance: { ratePercentText: '0', installmentsText: '1' },
    staff_loan: { ratePercentText: '0', installmentsText: '1' },
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    if (payrollSettings.state.status !== 'ready') return;
    setDraft(draftFromSettings(payrollSettings.state.settings));
  }, [payrollSettings.state]);

  if (payrollSettings.state.status !== 'ready') {
    return (
      <PayrollSettingsStateGate
        state={payrollSettings.state}
        retry={payrollSettings.retry}
        t={t}
      />
    );
  }

  // Captured as its own const (not re-read as `payrollSettings.state.settings`
  // inside `handleSave` below) -- TypeScript's narrowing of the
  // `status !== 'ready'` guard above does not carry through a member
  // expression into a nested function body, only through a plain local
  // identifier.
  const seededSettings = payrollSettings.state.settings;
  const canWrite = payrollSettings.save !== undefined;

  const rateBps = {
    salary_advance: parsePercentToBasisPoints(
      draft.salary_advance.ratePercentText,
    ),
    staff_loan: parsePercentToBasisPoints(draft.staff_loan.ratePercentText),
  };
  const installments = {
    salary_advance: parseIntegerInput(draft.salary_advance.installmentsText),
    staff_loan: parseIntegerInput(draft.staff_loan.installmentsText),
  };
  const rateValid = {
    salary_advance:
      rateBps.salary_advance !== null &&
      isValidAnnualRateBps(rateBps.salary_advance),
    staff_loan:
      rateBps.staff_loan !== null && isValidAnnualRateBps(rateBps.staff_loan),
  };
  const installmentsValid = {
    salary_advance:
      installments.salary_advance !== null &&
      isValidInstallmentCount(installments.salary_advance),
    staff_loan:
      installments.staff_loan !== null &&
      isValidInstallmentCount(installments.staff_loan),
  };
  const allValid =
    rateValid.salary_advance &&
    rateValid.staff_loan &&
    installmentsValid.salary_advance &&
    installmentsValid.staff_loan;

  async function handleSave(): Promise<void> {
    if (!payrollSettings.save || !allValid) return;
    const patch = buildLoanPolicyPatch(seededSettings, {
      // Non-null: gated on `allValid` above, which already checked every one
      // of these four parses succeeded.
      salaryAdvanceRateBps: rateBps.salary_advance as number,
      salaryAdvanceMaxInstallments: installments.salary_advance as number,
      staffLoanRateBps: rateBps.staff_loan as number,
      staffLoanMaxInstallments: installments.staff_loan as number,
    });
    if (isEmptyPatch(patch)) {
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
      }, 2000);
      return;
    }
    setIsSaving(true);
    setSaveFailed(false);
    const result = await payrollSettings.save(patch);
    setIsSaving(false);
    if (result.outcome === 'success') {
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
      }, 2000);
    } else {
      setSaveFailed(true);
    }
  }

  function handleCancel(): void {
    if (payrollSettings.settings) {
      setDraft(draftFromSettings(payrollSettings.settings));
    }
    setSaveFailed(false);
  }

  return (
    <GlassCard className="rounded-2xl p-5">
      <div className="grid grid-cols-1 gap-5">
        {(['salary_advance', 'staff_loan'] as const).map((kind, index) => (
          <div key={kind}>
            {index > 0 && (
              <div className="border-t border-[color:var(--line)] mb-5" />
            )}
            <p className="font-semibold mb-2.5">{t(LOAN_KIND_KEY[kind])}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="nv-label">
                  {t('data_centre.loan_annual_rate_label')}
                </label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={draft[kind].ratePercentText}
                  disabled={!canWrite}
                  error={!rateValid[kind]}
                  onChange={(e) => {
                    setDraft((current) => ({
                      ...current,
                      [kind]: {
                        ...current[kind],
                        ratePercentText: e.target.value,
                      },
                    }));
                  }}
                />
                {!rateValid[kind] && (
                  <p className="nv-help is-error">
                    {t('data_centre.rate_invalid')}
                  </p>
                )}
              </div>
              <div>
                <label className="nv-label">
                  {t('data_centre.loan_max_installments_label')}
                </label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={draft[kind].installmentsText}
                  disabled={!canWrite}
                  error={!installmentsValid[kind]}
                  onChange={(e) => {
                    setDraft((current) => ({
                      ...current,
                      [kind]: {
                        ...current[kind],
                        installmentsText: e.target.value,
                      },
                    }));
                  }}
                />
                {!installmentsValid[kind] && (
                  <p className="nv-help is-error">
                    {t('data_centre.installments_invalid')}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {canWrite && (
        <div className="flex items-center justify-end gap-3 mt-4">
          {saveFailed && (
            <p className="nv-help is-error mt-0">
              {t('payroll_settings.save_error')}
            </p>
          )}
          <Button variant="ghost" onClick={handleCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            loading={isSaving}
            disabled={!allValid}
          >
            {saved ? t('settings.saved') : t('settings.save')}
          </Button>
        </div>
      )}
    </GlassCard>
  );
}
